// Дашборд собственника по ЖК (макет заказчика 22.10.2026): ключевые показатели, корпуса, цикл этажа по этажам,
// что требует решения, узкие места, ежедневный отчёт и исходные данные. Себестоимость и финансы — отдельным этапом.

import { fmtDate, fmtNum, plural } from "../../shared/format";
import { useLive } from "../../shared/live";
import { navigate } from "../../shared/router";
import { TopBar } from "../../shared/shell";
import { Button, Icon, Loading, Select, useAsync } from "../../shared/ui";
import { CycleLines, Ring } from "../charts";
import { api, can, floorName, isStage, live, useCtx } from "../ctx";
import { PendingPanel, StateTag } from "../lifecycle";

export function OverviewPage({ projectId, projects }: { projectId: string; projects: { id: string; name: string }[] }) {
  const d = useAsync<any>(() => api.get(`/projects/${projectId}/overview`), [projectId]);
  useLive(live, ["report.", "cycle.", "issue."], () => d.reload(), 2000);
  const v = d.data;
  const { me } = useCtx();
  return (
    <>
      <TopBar title={v ? `Обзор · ${v.project.name}` : "Обзор"}>
        {projects.length > 1 && (
          <Select aria-label="Проект" value={projectId} style={{ width: 220 }} onChange={(e) => navigate(`/p/${e.target.value}`)}
            options={projects.map((p) => ({ value: p.id, label: p.name }))} />
        )}
      </TopBar>
      <div className="content prod">
        {can(me, "projects.approve") && <PendingPanel projectId={projectId} />}
        {!v ? <Loading error={d.error} onRetry={d.reload} /> : (
          <>
            <div className="kpi-row six">
              <div className="kpi wide">
                <div className="k">1. Готовность ЖК</div>
                <div className="row" style={{ gap: 12, flexWrap: "nowrap" }}>
                  <Ring value={v.kpi.progress} />
                  <div className="small"><div className="muted">план на сегодня</div><b style={{ fontSize: 17 }}>{fmtNum(v.kpi.progress_plan, 0)}%</b>
                    <Delta v={v.kpi.progress - v.kpi.progress_plan} unit="%" goodUp /></div>
                </div>
              </div>
              <div className="kpi">
                <div className="k">2. Текущий этаж</div>
                <div className="stack tight" style={{ marginTop: 4 }}>
                  {v.buildings.map((b: any) => (
                    <div key={b.id} className="small"><span className="muted">{b.name}</span><br />
                      <b style={{ fontSize: 18 }}>{b.started ? `${b.floor_current ?? b.floors} / ${b.floors}` : "Подготовка"}</b></div>
                  ))}
                </div>
              </div>
              <div className="kpi">
                <div className="k">3. Цикл этажа</div>
                <div className="v">{v.kpi.cycle_avg != null ? `${fmtNum(v.kpi.cycle_avg, 1)} дн.` : "—"}</div>
                <div className="s">план {fmtNum(v.kpi.cycle_plan, 0)} дн.</div>
                {v.kpi.cycle_avg != null && v.kpi.cycle_plan != null && <Delta v={v.kpi.cycle_avg - v.kpi.cycle_plan} unit=" дн." />}
              </div>
              <PlanFact title="4. Бетон, м³" v={v.kpi.concrete} digits={0} />
              <PlanFact title="5. Арматура, т" v={v.kpi.rebar} digits={1} />
            </div>

            <div className="owner-grid">
              <div className="panel area-objects">
                <div className="ph"><h2>Корпуса</h2></div>
                <div className="pb objects">
                  {v.buildings.map((b: any) => <BuildingCard key={b.id} b={b} />)}
                </div>
              </div>
              <div className="panel area-cycle">
                <div className="ph"><h2 className="grow">Цикл этажа, дни</h2>
                  {v.kpi.cycle_avg != null && <span className="small muted">средний <b>{fmtNum(v.kpi.cycle_avg, 2)}</b> при плане {fmtNum(v.kpi.cycle_plan, 0)}</span>}</div>
                <div className="pb stack tight">
                  <CycleLines norm={v.kpi.cycle_plan}
                    series={v.cycles.filter((c: any) => c.floors.some((f: any) => f.actual)).map((c: any) => ({ name: c.building, points: c.floors.map((f: any) => ({ floor: f.floor, days: f.actual, forecast: f.forecast })) }))} />
                  <div className="legend small">
                    {v.cycles.filter((c: any) => c.floors.some((f: any) => f.actual)).map((c: any, i: number) => <span key={c.id}><i className={`sw-ser-${i + 1}`} />{c.building}</span>)}
                    <span><i className="sw-norm" />план</span><span className="muted">полая точка — прогноз идущего этажа</span>
                  </div>
                </div>
              </div>
              <div className="panel area-decide decide">
                <div className="ph"><h2 className="grow">Требует моего решения</h2><span className="tag s-conflict">{v.decisions.length}</span></div>
                <div className="pb stack tight">
                  {v.decisions.length === 0 && <p className="muted" style={{ margin: 0 }}>Открытых вопросов нет.</p>}
                  {v.decisions.slice(0, 4).map((r: any, i: number) => (
                    <div key={r.id} className="dec">
                      <div className="grow"><b>{i + 1}. {r.title}</b><div className="small muted">{[r.building, r.where, r.detail].filter(Boolean).join(" · ")}</div></div>
                      <Button size="small" variant="danger" onClick={() => navigate(`/b/${r.building_id}/issues${r.kind === "plan_request" ? "?tab=requests" : ""}`)}>Открыть</Button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="panel area-bottle">
                <div className="ph"><h2 className="grow">Узкие места производства</h2><span className="small muted">простой за {v.bottlenecks.days} дн.</span></div>
                <div className="pb stack tight">
                  {v.bottlenecks.rows.length === 0 && <p className="muted" style={{ margin: 0 }}>Простоев за неделю нет.</p>}
                  {v.bottlenecks.rows.map((r: any) => (
                    <div key={r.name} className="bn">
                      <span className="grow">{r.name}</span>
                      <span className="bnbar"><i style={{ width: `${r.share}%` }} /></span>
                      <span className="num small">{fmtNum(r.hours, 1)} ч · {r.share}%</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="panel area-daily">
                <div className="ph"><h2 className="grow">Ежедневный отчёт</h2>{v.daily && <span className="small muted">{fmtDate(v.daily.date)}</span>}</div>
                {!v.daily ? <p className="muted pb">Отчётов ещё нет.</p> : (
                  <div className="pb daily">
                    <div className="stack tight grow">
                      {v.daily.rows.map((r: any) => (
                        <div key={r.building_id} className="stack tight small">
                          <b>{r.building}, {floorName(r.floor, false)}</b>
                          <div>Готовность этажа — {fmtNum(r.cycle_progress, 0)}%</div>
                          <div>Люди: план {r.people_plan}, факт {r.people_fact}</div>
                          {r.downtime_hours > 0 && <div style={{ color: "var(--st-loading)" }}>Простой {fmtNum(r.downtime_hours, 1)} ч{r.downtime_reasons.length ? `: ${r.downtime_reasons.join(", ").toLowerCase()}` : ""}</div>}
                          {r.issues_new && <div style={{ color: "var(--st-conflict)" }}>Есть новые проблемы</div>}
                          {r.plan_tomorrow && <div className="muted">Завтра: {r.plan_tomorrow}</div>}
                        </div>
                      ))}
                    </div>
                    {v.daily.photo && (
                      <button className="photo-card" onClick={() => navigate(`/b/${v.daily.rows[0]?.building_id || v.buildings[0].id}/photos`)}>
                        <img src={api.base + `/photos/${v.daily.photo.id}/file?thumb=true&token=${api.token}`} alt={v.daily.photo.caption || "Фото с объекта"} />
                        <span className="small">{v.daily.photo.caption || "Фото"}{v.daily.photo.floor != null ? ` · ${floorName(v.daily.photo.floor, false)}` : ""}</span>
                        <span className="small link">Фотоотчёты</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="source">
              <div className="src-item"><Icon name="building" /><div><div className="k">Проект</div><b>{v.project.name}</b></div></div>
              <div className="src-item"><Icon name="schedule" /><div><div className="k">Начало строительства</div><b>{fmtDate(v.project.planned_start)}</b></div></div>
              <div className="src-item"><Icon name="plan" /><div><div className="k">Окончание каркаса: план / прогноз</div><b>{fmtDate(v.project.planned_end)} / {fmtDate(v.project.forecast_end)}</b></div></div>
              {v.source.map((s: any) => (
                <div key={s.building} className="src-item"><Icon name="grid" /><div><div className="k">{s.building}: типовой этаж</div>
                  <b>{s.typical_concrete_m3 != null ? `${fmtNum(s.typical_concrete_m3, 0)} м³` : "—"}{s.typical_rebar_t != null ? ` · ${fmtNum(s.typical_rebar_t, 1)} т` : ""}{s.rebar_kg_per_m3 ? ` · ${s.rebar_kg_per_m3} кг/м³` : ""}</b>
                  <div className="k">{s.floors} {plural(s.floors, "этаж", "этажа", "этажей")}, {s.shifts === 2 ? "2 смены" : "1 смена"}</div></div></div>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}

function Delta({ v, unit, goodUp }: { v: number; unit: string; goodUp?: boolean }) {
  if (Math.abs(v) < 0.05) return <div className="s">в плане</div>;
  const good = goodUp ? v > 0 : v < 0;
  return <div className={`s ${good ? "good" : "bad"}`}>{v > 0 ? "+" : ""}{fmtNum(v, 1)}{unit} {v > 0 ? "↑" : "↓"}</div>;
}

function PlanFact({ title, v, digits }: { title: string; v: { plan: number; fact: number }; digits: number }) {
  const dev = v.plan ? ((v.fact - v.plan) / v.plan) * 100 : 0;
  return (
    <div className="kpi">
      <div className="k">{title}</div>
      <div className="pf"><span className="muted">план к сегодня</span><b>{fmtNum(v.plan, digits)}</b></div>
      <div className="pf"><span className="muted">факт</span><b>{fmtNum(v.fact, digits)}</b></div>
      <div className={`s ${Math.abs(dev) < 2 ? "" : dev < 0 ? "bad" : "good"}`}>{dev > 0 ? "+" : ""}{fmtNum(dev, 1)}%</div>
    </div>
  );
}

function BuildingCard({ b }: { b: any }) {
  const behind = b.progress.fact - b.progress.plan;
  return (
    <button className="obj" onClick={() => navigate(`/b/${b.id}`)}>
      <div className="row between"><b style={{ fontSize: 16 }}>{b.name} <StateTag state={b.state} /></b><span className="small muted">{b.floors} этажей</span></div>
      {b.started ? (
        <>
          <div className="row" style={{ gap: 12, flexWrap: "nowrap" }}>
            <Ring value={b.progress.fact} size={64} />
            <div className="small"><div className="muted">план {fmtNum(b.progress.plan, 0)}%</div>
              <div className={behind < -0.5 ? "bad" : "good"} style={{ fontWeight: 700 }}>{behind > 0 ? "+" : ""}{fmtNum(behind, 1)}%</div>
              <div className="muted">{isStage(b.floor_current) ? floorName(b.floor_current) : `этаж ${b.floor_current ?? "—"} из ${b.floors}`}</div></div>
          </div>
          <div className="metrics">
            {b.metrics.map((m: any) => (
              <div key={m.name} className="m"><span className={`dot ${m.value >= 95 ? "" : m.value >= 80 ? "warn" : "off"}`} />{m.name}<b className="num">{m.value}%</b></div>
            ))}
          </div>
          <div className="small muted">Вахта: {b.crew_plan} чел. в смену · окончание {fmtDate(b.forecast_end)}</div>
        </>
      ) : (
        <>
          <div className="tag s-planned" style={{ alignSelf: "flex-start" }}>Подготовка</div>
          <div className="small">Старт {fmtDate(b.start_date)}{b.starts_in_days != null && b.starts_in_days > 0 ? ` — через ${b.starts_in_days} ${plural(b.starts_in_days, "день", "дня", "дней")}` : ""}</div>
          <div className="small muted">Вахта: {b.crew_plan} чел. в смену · окончание по плану {fmtDate(b.planned_end)}</div>
        </>
      )}
    </button>
  );
}
